import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as brandActions from "src/redux/slices/brandSlice";
import { Brand } from "src/types/brand.types";

interface AddBrandModalProps {
  brand: Brand | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AddBrandModal: React.FC<AddBrandModalProps> = ({
  brand,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!brand?._id;
  const [name, setName] = useState(brand?.name || "");
  const [touched, setTouched] = useState(false);
  const dispatch = useAppDispatch();
  const { createBrand, editBrand } = useAppSelector((state) => state.brand);
  const loading = isEdit ? editBrand.loading : createBrand.loading;

  useEffect(() => {
    if (isEdit && editBrand.data) {
      toast.success("Brand updated successfully");
      dispatch(brandActions.resetEditBrand());
      onSuccess();
    } else if (isEdit && editBrand.error) {
      toast.error(`Error: ${editBrand.error}`);
      dispatch(brandActions.resetEditBrand());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBrand]);

  useEffect(() => {
    if (!isEdit && createBrand.data) {
      toast.success("Brand created successfully");
      dispatch(brandActions.resetCreateBrand());
      onSuccess();
    } else if (!isEdit && createBrand.error) {
      toast.error(`Error: ${createBrand.error}`);
      dispatch(brandActions.resetCreateBrand());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createBrand]);

  const handleSubmit = () => {
    setTouched(true);
    if (!name.trim()) return;
    if (isEdit) {
      dispatch(brandActions.editBrandRequest({ _id: brand!._id, name: name.trim() }));
    } else {
      dispatch(brandActions.createBrandRequest({ name: name.trim() }));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edit brand" : "Add brand"}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading} disabled={loading}>
            {isEdit ? "Save changes" : "Add brand"}
          </Button>
        </>
      }
    >
      <Input
        label="Brand name"
        placeholder="e.g. Peter England"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        error={touched && !name.trim()}
        helperText={touched && !name.trim() ? "Brand name is required" : undefined}
      />
    </Modal>
  );
};

export default AddBrandModal;
